import { Box3, Group, Vector3 } from 'three';

import type { SceneComposition, SceneObject } from '../../../types/engine.types';
import type { ApplianceSceneBuilder } from '../appliance-scene-builder.class';
import type { ApplianceInput } from '../types';

/**
 * Build scene composition from appliance configuration
 */
export const buildCfn = async (
	self: ApplianceSceneBuilder,
	{ input }: { input: ApplianceInput }
): Promise<SceneComposition> => {
	const objects: SceneObject[] = [];

	// Create appliance container group
	const group = new Group();

	// Position, rotation, and scale are applied after the model loads (see below)
	// so the appliance can first be recentered on the group origin.

	// Set userData
	const id = input.id ?? `appliance-${Date.now()}`;
	group.userData = {
		id,
		type: 'appliance',
		subtype: input.type ?? input.placement,
		sku: input.sku,
		modelPath: input.modelPath ?? (input.sku ? `models/${input.sku}.dae` : undefined),
		size: input.size
	};
	group.name = id;

	// Load the 3D model into the group
	const modelPath = input.modelPath ?? (input.sku ? `models/${input.sku}.dae` : undefined);
	const { modelManager } = self.configAccess;

	if (modelManager && modelPath) {
		try {
			const loadedModels = await modelManager.loadModels([{
				path: modelPath,
				position: { x: 0, y: 0, z: 0 },
				rotation: { x: 0, y: 0, z: 0 },
				metadata: {
					id,
					type: 'appliance' as const,
					sku: input.sku,
					subtype: input.type ?? input.placement
				}
			}]);

			for (const model of loadedModels) {
				group.add(model);
			}
		} catch (error) {
			console.warn('ApplianceSceneBuilder: Failed to load model:', modelPath, error);
		}
	}

	// Appliance models load with their pivot at a footprint corner rather than
	// their geometric center, so a single-appliance viewer would otherwise frame
	// and orbit an edge. Recenter the loaded geometry on the group origin (and
	// rest it on the ground) while the group is still untransformed.
	if (group.children.length > 0) {
		const modelBounds = new Box3().setFromObject(group);

		if (!modelBounds.isEmpty()) {
			const modelCenter = modelBounds.getCenter(new Vector3());

			for (const child of group.children) {
				child.position.x -= modelCenter.x;
				child.position.y -= modelBounds.min.y;
				child.position.z -= modelCenter.z;
			}
		}
	}

	// Apply the requested transform after recentering, so `position` places the
	// appliance's true center (not a corner) at the requested point.
	if (input.position) {
		group.position.set(input.position.x, input.position.y, input.position.z);
	}

	if (input.rotation) {
		group.rotation.set(input.rotation.x, input.rotation.y, input.rotation.z);
	}

	if (input.scale) {
		group.scale.setScalar(input.scale);
	}

	objects.push({
		object: group,
		metadata: {
			id,
			type: 'appliance',
			subtype: input.type ?? input.placement,
			source: self.name
		}
	});

	// Calculate bounds based on size or defaults
	const pos = input.position ?? { x: 0, y: 0, z: 0 };
	const halfWidth = (input.size?.width ?? 24) / 2;
	const halfDepth = (input.size?.depth ?? 24) / 2;
	const height = input.size?.height ?? 36;

	const bounds = {
		min: {
			x: pos.x - halfWidth,
			y: pos.y,
			z: pos.z - halfDepth
		},
		max: {
			x: pos.x + halfWidth,
			y: pos.y + height,
			z: pos.z + halfDepth
		}
	};

	// Camera positioned to view the appliance
	const maxDim = Math.max(halfWidth * 2, height, halfDepth * 2);
	const cameraDistance = maxDim * 2;

	return {
		objects,
		bounds,
		camera: {
			position: {
				x: pos.x,
				y: pos.y + height * 0.6,
				z: pos.z + cameraDistance
			},
			target: {
				x: pos.x,
				y: pos.y + height * 0.4,
				z: pos.z
			}
		}
	};
};
